if (!customElements.get('main-product-gallery')) {
  customElements.define(
    'main-product-gallery',
    class MainProductGallery extends HTMLElement {
      constructor() {
        super();

        this.elementId = this.id || this.getAttribute('id');
        this.mainCarouselSelector = `#${this.elementId} #main-carousel`;
        this.thumbnailCarouselSelector = `#${this.elementId} #thumbnail-carousel`;

        this.imageHeight = this.querySelector('#main-carousel ul li img')?.height || 500;

        this.thumbnailPosition = this.dataset.thumbnailPosition;
        this.desktopLayout = this.dataset.desktopLayout || 'thumbnail_slider';
        this.mobileLayout = this.dataset.mobileLayout || 'hide';

        this.variants = this.getVariants();

        this.init();
        this.syncedVariantImage = this.dataset.syncedVariantImage;
        this.variantShouldTrigger = true;
      }

      connectedCallback() {
        const boundHandleVariantChange = this.handleVariantChange.bind(this);
        subscribe(PUB_SUB_EVENTS.variantChange, (variant) => boundHandleVariantChange({ data: { variant } }));
      }

      init() {
        this.convertVariantsToArray();

        const isDesktop = window.matchMedia('(min-width: 989px)').matches;
        const isMobile = window.matchMedia('(max-width: 988px)').matches;

        // Determine if we need slider functionality based on layout
        const needsMainSlider = this.shouldUseMainSlider(isDesktop, isMobile);
        const needsThumbnailSlider = this.shouldUseThumbnailSlider(isDesktop, isMobile);

        // Initialize main slider
        if (needsMainSlider) {
          const mainCarousel = this.querySelector(this.mainCarouselSelector);
          this.mainSlider = new Splide(mainCarousel, {
            type: 'loop',
            rewind: true,
            pagination: false,
            arrows: mainCarousel?.dataset.showArrows === 'true',
            autoHeight: true,
            perPage: 2,
            gap: 10,
            perMove: 1,
            heightRatio: 1,
            breakpoints: {
              768: {
                perPage: 1,
              },
            },
            classes: {
              arrows: 'splide__arrows slider-buttons',
              arrow: 'slider-button splide__arrow',
              prev: 'splide__arrow--prev',
              next: 'splide__arrow--next',
            },
          });
        }

        // Handle thumbnail visibility for mobile
        if (isMobile) {
          const thumbnailCarousel = this.querySelector(this.thumbnailCarouselSelector);
          if (thumbnailCarousel) {
            if (this.mobileLayout === 'hide') {
              thumbnailCarousel.classList.add('hidden');
            } else {
              thumbnailCarousel.classList.remove('hidden');
            }
          }
        } else {
          const thumbnailCarousel = this.querySelector(this.thumbnailCarouselSelector);
          if (thumbnailCarousel) {
            thumbnailCarousel.classList.remove('hidden');
          }
        }

        // Initialize thumbnail slider if needed
        if (needsThumbnailSlider) {
          const defaultOptions = {
            perPage: 5,
            gap: 10,
            fixedWidth: 60,
            fixedHeight: 60,
            pagination: false,
            dragMinThreshold: 10,
            isNavigation: needsMainSlider,
            arrows: this.querySelector(this.thumbnailCarouselSelector)?.dataset.showArrows === 'true',
            classes: {
              arrows: 'splide__arrows slider-buttons',
              arrow: 'slider-button splide__arrow',
              prev: 'splide__arrow--prev',
              next: 'splide__arrow--next',
            },
          };

          const leftRightOptions = {
            height: `${this.imageHeight}px`,
            direction: 'ttb',
            autoHeight: true,
            autoWidth: true,
            heightRatio: 1,
          };

          if (isDesktop) {
            const thumbnailTrack = this.querySelector('#thumbnail-carousel .splide__track');
            if (thumbnailTrack) {
              thumbnailTrack.style.setProperty('--active-slide-height', `${this.imageHeight}px`);
            }

            if (this.thumbnailPosition === 'bottom') {
              this.thumbnailSlider = new Splide(this.querySelector(this.thumbnailCarouselSelector), {
                ...defaultOptions,
              });
            } else if (this.thumbnailPosition === 'left' || this.thumbnailPosition === 'right') {
              this.thumbnailSlider = new Splide(this.querySelector(this.thumbnailCarouselSelector), {
                ...defaultOptions,
                ...leftRightOptions,
              });
            } else {
              this.thumbnailSlider = new Splide(this.querySelector(this.thumbnailCarouselSelector), {
                ...defaultOptions,
              });
            }
          } else if (isMobile) {
            this.thumbnailSlider = new Splide(this.querySelector(this.thumbnailCarouselSelector), {
              ...defaultOptions,
            });
          }

          this.bar = this.querySelector('.thumbnail-carousel__progress-bar');
          this.imageCountElement = this.querySelector('.thumbnail-carousel__image-count');

          if (this.thumbnailSlider) {
            this.thumbnailSlider.on('mounted move', () => {
              const end = this.thumbnailSlider.Components.Controller.getEnd() + 1;
              const length = this.thumbnailSlider.length;
              const rate = Math.min((this.thumbnailSlider.index + 1) / end, 1);

              if (this.bar) {
                this.bar.style.width = `${100 * rate}%`;
              }

              if (this.imageCountElement) {
                this.imageCountElement.innerHTML = `
    <span class="thumbnail-carousel__image-count__current">
      ${String(this.thumbnailSlider.index + 1).padStart(2, '0')}
    </span>/
    <span class="thumbnail-carousel__image-count__total">
      ${String(length).padStart(2, '0')}
    </span>
  `;
              }
            });
          }
        } else {
          this.progressBar = this.querySelector('.dbtfy-carousel__progress_counter');
          if (this.progressBar) {
            this.progressBar.remove();
          }
        }

        // Sync sliders if both exist
        if (needsMainSlider && needsThumbnailSlider && this.mainSlider && this.thumbnailSlider) {
          this.mainSlider.sync(this.thumbnailSlider);
        }

        // Mount sliders
        if (this.mainSlider) {
          this.mainSlider.on('mounted', () => {
            const currentIndex = this.mainSlider.index;
            const slides = this.mainSlider.Components.Elements.slides;
            const currentSlide = slides[currentIndex];
            if (currentSlide) {
              const slideHeight =
                currentSlide.querySelector('img')?.offsetHeight || currentSlide.querySelector('video')?.offsetHeight;
              this.style.setProperty('--active-slide-height', `${slideHeight}px`);
            }
          });

          this.mainSlider.mount();
        }

        if (this.thumbnailSlider) {
          this.thumbnailSlider.mount();
        }

        // Add event listeners for main slider
        if (this.mainSlider) {
          this.mainSlider.on('move', (newIndex) => {
            const currentSlide = newIndex;
            const currentSlideElement = this.mainSlider.Components.Elements.slides;

            if (currentSlideElement) {
              const currentSlideItem = currentSlideElement[currentSlide];
              if (currentSlideItem) {
                const slideHeight =
                  currentSlideItem.querySelector('img')?.offsetHeight ||
                  currentSlideItem.querySelector('video')?.offsetHeight;
                if (slideHeight) {
                  this.style.setProperty('--active-slide-height', `${slideHeight}px`);
                }
              }
            }

            // Sync thumbnail active state for layouts with static thumbnails
            if (!this.thumbnailSlider) {
              this.updateThumbnailActiveState(newIndex);
            }
          });
        }

        // Add event listeners for thumbnail slider
        if (this.thumbnailSlider) {
          this.thumbnailSlider.on('moved', (newIndex) => {
            if (Debutify.settings.dbtfy_variant_sync_on_media) {
              if (!this.variantShouldTrigger) {
                this.variantShouldTrigger = true;
                return;
              }
              this.variantShouldTrigger = true;

              const mediaId = this.thumbnailSlider.Components.Elements.slides[newIndex].dataset.mediaId;

              const currentVariantOptions = this.variants?.[mediaId] || null;

              if (currentVariantOptions) {
                this.syncedVariantImage = 'false'; // temporary flag to prevent triggering the event

                const variantSelects = this.closest('product-info').querySelectorAll('variant-selects fieldset');

                currentVariantOptions.forEach((option, index) => {
                  const variantSelect = variantSelects[index];
                  if (variantSelect) {
                    const radio = variantSelect.querySelector(`input[value="${option}"]`);
                    if (radio) {
                      radio.checked = true;
                      radio.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                });
                this.syncedVariantImage = 'true';
              }
            }
          });
        }

        // For layouts with static thumbnails (thumbnail layout), add click handlers
        if ((isDesktop && this.desktopLayout === 'thumbnail') || (isMobile && this.mobileLayout === 'show')) {
          this.setupThumbnailClickHandlers();
        }
      }

      setupThumbnailClickHandlers() {
        const thumbnails = this.querySelectorAll('#thumbnail-carousel .splide__slide');
        thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => {
            // Remove active class from all thumbnails
            thumbnails.forEach((t) => t.classList.remove('is-active'));
            // Add active class to clicked thumbnail
            thumbnail.classList.add('is-active');

            if (this.mainSlider) {
              const position = parseInt(thumbnail.dataset.position) - 1;
              this.mainSlider.go(position);
            }
          });
        });

        // Set initial active state
        if (thumbnails.length > 0) {
          thumbnails[0].classList.add('is-active');
        }
      }

      updateThumbnailActiveState(activeIndex) {
        const thumbnails = this.querySelectorAll('#thumbnail-carousel .splide__slide');
        thumbnails.forEach((thumbnail, index) => {
          const position = parseInt(thumbnail.dataset.position) - 1;
          if (position === activeIndex) {
            thumbnail.classList.add('is-active');
          } else {
            thumbnail.classList.remove('is-active');
          }
        });
      }

      shouldUseMainSlider(isDesktop, isMobile) {
        // Desktop layouts that need main slider
        if (isDesktop) {
          return ['thumbnail', 'thumbnail_slider'].includes(this.desktopLayout);
        }
        // Mobile layouts that need main slider
        if (isMobile) {
          return ['show', 'hide'].includes(this.mobileLayout);
        }
        return true;
      }

      shouldUseThumbnailSlider(isDesktop, isMobile) {
        // Desktop layouts that need thumbnail slider
        if (isDesktop) {
          return this.desktopLayout === 'thumbnail_slider';
        }
        // Mobile layouts that need thumbnail slider
        if (isMobile) {
          return this.mobileLayout === 'show';
        }
        return false;
      }

      getVariants() {
        const productInfo = this.closest('product-info');
        const productVariants = productInfo.querySelector('variant-selects [data-product-variants]')?.innerHTML;
        return !!productVariants ? JSON.parse(productVariants) : null;
      }

      convertVariantsToArray() {
        if (!this.variants) return;

        this.variants = Object.entries(this.variants)
          .filter(([key, value]) => value.featured_media?.id) // Check if featured_media.id exists
          .reduce((acc, [key, value]) => {
            acc[value.featured_media.id] = value.options;
            return acc;
          }, {});
      }

      handleVariantChange({ data: { variant } }) {
        this.variant = variant.data.variant;
        if (!this.variant.featured_media || this.syncedVariantImage !== 'true') return;
        const variantFeaturedMediaId = this.variant.featured_media.id;

        if (this.closest('.quick-add-modal')) {
          const productInfo = this.closest('product-info');
          if (productInfo) {
            this.sectionId = productInfo.dataset.originalSection;
          }
        } else {
          this.sectionId = this.dataset.sectionId;
        }

        if (variant.data.sectionId != this.sectionId) {
          return;
        }

        const isDesktop = window.matchMedia('(min-width: 989px)').matches;
        const isMobile = window.matchMedia('(max-width: 988px)').matches;

        // For layouts without sliders (columns, stacked), reorder images to show variant image first
        const needsReordering =
          (isDesktop && ['columns', 'stacked'].includes(this.desktopLayout)) ||
          (isMobile && this.mobileLayout === 'columns');

        if (needsReordering) {
          this.reorderMediaToShowVariantFirst(variantFeaturedMediaId);
          return;
        }

        // Find the media position
        let mediaPosition = this.querySelector(this.thumbnailCarouselSelector)?.querySelector(
          "[data-media-id='" + `${variantFeaturedMediaId}` + "']",
        )?.dataset.position;

        // If not found in thumbnail carousel, try main carousel
        if (!mediaPosition) {
          mediaPosition = this.querySelector(this.mainCarouselSelector)?.querySelector(
            "[data-media-id='" + `${variantFeaturedMediaId}` + "']",
          )?.dataset.position;
        }

        if (mediaPosition) {
          mediaPosition = parseInt(mediaPosition) - 1;

          // Update thumbnail slider if it exists
          if (this.thumbnailSlider && mediaPosition != this.thumbnailSlider.index) {
            this.variantShouldTrigger = false;
            this.thumbnailSlider.go(mediaPosition);
          }
          // Update main slider if it exists and thumbnail slider doesn't
          else if (this.mainSlider && !this.thumbnailSlider && mediaPosition != this.mainSlider.index) {
            this.mainSlider.go(mediaPosition);
          }
        }
      }

      reorderMediaToShowVariantFirst(variantFeaturedMediaId) {
        const mainCarousel = this.querySelector(this.mainCarouselSelector);
        if (!mainCarousel) return;

        const slidesList = mainCarousel.querySelector('.splide__list');
        if (!slidesList) return;

        // Find the slide with the variant's featured media
        const variantSlide = slidesList.querySelector(`[data-media-id="${variantFeaturedMediaId}"]`);
        if (!variantSlide) return;

        // If it's already first, no need to reorder
        const firstSlide = slidesList.querySelector('.splide__slide:first-child');
        if (firstSlide && firstSlide.dataset.mediaId === variantFeaturedMediaId) return;

        // Move the variant slide to the beginning
        slidesList.insertBefore(variantSlide, slidesList.firstChild);

        // Update position attributes for all slides
        const allSlides = slidesList.querySelectorAll('.splide__slide');
        allSlides.forEach((slide, index) => {
          slide.setAttribute('data-position', index + 1);
        });

        // Also reorder thumbnails if they exist
        const thumbnailCarousel = this.querySelector(this.thumbnailCarouselSelector);
        if (thumbnailCarousel) {
          const thumbnailList = thumbnailCarousel.querySelector('.splide__list');
          if (thumbnailList) {
            const variantThumbnail = thumbnailList.querySelector(`[data-media-id="${variantFeaturedMediaId}"]`);
            if (variantThumbnail) {
              thumbnailList.insertBefore(variantThumbnail, thumbnailList.firstChild);

              // Update position attributes for thumbnails
              const allThumbnails = thumbnailList.querySelectorAll('.splide__slide');
              allThumbnails.forEach((thumb, index) => {
                thumb.setAttribute('data-position', index + 1);
              });
            }
          }
        }
      }
    },
  );
}
